import { useMemo, useReducer } from 'react';
import { useAsync } from 'react-use';

import { SongPreview } from '~/interfaces';
import events from '~/modules/game-events/game-events';
import { useEventEffect } from '~/modules/game-events/hooks';
import { getAllStats, getSongKey } from '~/modules/songs/stats/common';

/**
 * The ids of songs this machine has a play recorded for, so the Played filter can ask the question
 * of the whole catalogue at once instead of once per card.
 *
 * Plays are filed under a storage key rather than a song id (`getSongKey`), so the lookup goes the
 * other way: build the key for each song and ask whether the stats hold it. Re-read when a play is
 * stored, so finishing a song moves it out of "Never played" without a reload.
 */
export default function usePlayedSongIds(list: SongPreview[]): string[] {
  const [playsRecorded, songWasPlayed] = useReducer((count: number) => count + 1, 0);
  useEventEffect(events.songStatStored, songWasPlayed);

  const stats = useAsync(() => getAllStats(), [playsRecorded]);

  return useMemo(() => {
    const played = stats.value;
    if (!played) return [];

    return list.filter((song) => (played[getSongKey(song)]?.plays ?? 0) > 0).map((song) => song.id);
  }, [list, stats.value]);
}
